import { useState } from "react";
import { exportSvgToVideo, pickBestMimeType } from "../../utils/exportVideo";

export default function VideoExportPanel({ svgRef, engine, duration, backgroundColor, onClose, onDone }) {
  const [fps, setFps] = useState(30);
  const [scale, setScale] = useState(2);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | rendering | done | error
  const [error, setError] = useState(null);
  const [resultUrl, setResultUrl] = useState(null);
  const [resultExt, setResultExt] = useState("webm");

  const supportedMime = pickBestMimeType();
  const willBeMp4 = supportedMime?.startsWith("video/mp4");

  const handleRender = async () => {
    if (!svgRef.current) {
      setError("Canvas SVG belum siap.");
      setStatus("error");
      return;
    }
    setStatus("rendering");
    setProgress(0);
    setError(null);
    try {
      const { blob, extension } = await exportSvgToVideo(svgRef.current, engine, duration, {
        fps,
        scale,
        backgroundColor,
        onProgress: setProgress,
      });
      const url = URL.createObjectURL(blob);
      setResultUrl(url);
      setResultExt(extension);
      setStatus("done");
      onDone?.();
    } catch (err) {
      setError(err.message || "Gagal render video.");
      setStatus("error");
    }
  };

  const handleDownload = () => {
    if (!resultUrl) return;
    const a = document.createElement("a");
    a.href = resultUrl;
    a.download = `animation.${resultExt}`;
    a.click();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-8" onClick={onClose}>
      <div
        className="bg-[#0F0F0F] border border-[#145A43] rounded-lg w-full max-w-md flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#145A43]/40">
          <h3 className="text-[#D7C7A1] text-sm font-medium">Export Video</h3>
          <button onClick={onClose} className="text-[#D7C7A1]/60 hover:text-[#D7C7A1] text-sm">
            ✕
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div
            className={`text-xs rounded px-3 py-2 ${
              willBeMp4
                ? "bg-[#2F8F6F]/15 text-[#D7C7A1]"
                : "bg-yellow-500/10 text-yellow-300/90"
            }`}
          >
            {willBeMp4
              ? "Browser ini mendukung export .mp4 secara native."
              : "Browser ini cuma bisa export native ke .webm (bukan .mp4). File .webm tetap bisa diputar di hampir semua tempat, termasuk diupload ke platform lain."}
          </div>

          {backgroundColor === null && (
            <div className="text-xs rounded px-3 py-2 bg-yellow-500/10 text-yellow-300/90">
              Background diset transparan. Codec video pada umumnya (H.264/VP8) tidak
              mendukung channel alpha, jadi bagian transparan kemungkinan besar tetap
              muncul solid (biasanya hitam) di file video hasil export. Kalau butuh
              transparansi asli, export ke CSS/SVG lebih cocok — atau pilih warna
              background dulu sebelum render video.
            </div>
          )}

          {status !== "done" && (
            <>
              <div className="flex items-center gap-3">
                <label className="text-xs text-[#D7C7A1]/60 w-16">FPS</label>
                <select
                  value={fps}
                  onChange={(e) => setFps(parseInt(e.target.value, 10))}
                  disabled={status === "rendering"}
                  className="flex-1 bg-[#0F0F0F] border border-[#145A43] rounded px-2 py-1 text-sm text-[#D7C7A1] focus:outline-none focus:border-[#2F8F6F]"
                >
                  <option value={24}>24</option>
                  <option value={30}>30</option>
                  <option value={60}>60</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs text-[#D7C7A1]/60 w-16">Resolusi</label>
                <select
                  value={scale}
                  onChange={(e) => setScale(parseInt(e.target.value, 10))}
                  disabled={status === "rendering"}
                  className="flex-1 bg-[#0F0F0F] border border-[#145A43] rounded px-2 py-1 text-sm text-[#D7C7A1] focus:outline-none focus:border-[#2F8F6F]"
                >
                  <option value={1}>1x (asli)</option>
                  <option value={2}>2x</option>
                  <option value={3}>3x</option>
                </select>
              </div>
            </>
          )}

          {status === "rendering" && (
            <div>
              <div className="h-2 bg-[#145A43]/40 rounded overflow-hidden">
                <div
                  className="h-full bg-[#2F8F6F] transition-all"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
              </div>
              <p className="text-xs text-[#D7C7A1]/50 mt-1.5">
                Merender frame... {Math.round(progress * 100)}%
              </p>
            </div>
          )}

          {status === "error" && <p className="text-red-400 text-xs">{error}</p>}

          {status === "done" && (
            <div className="space-y-2">
              <p className="text-xs text-[#D7C7A1]/70">
                Video selesai dirender ({resultExt.toUpperCase()}).
              </p>
              {resultUrl && (
                <video src={resultUrl} controls className="w-full rounded border border-[#145A43]" />
              )}
            </div>
          )}
        </div>

        <div className="flex gap-2 px-4 py-3 border-t border-[#145A43]/40">
          {status !== "done" ? (
            <button
              onClick={handleRender}
              disabled={status === "rendering"}
              className="flex-1 text-xs py-2 rounded bg-[#2F8F6F] text-[#0F0F0F] font-medium hover:bg-[#3aa87f] transition-colors disabled:opacity-50"
            >
              {status === "rendering" ? "Merender..." : "Render Video"}
            </button>
          ) : (
            <button
              onClick={handleDownload}
              className="flex-1 text-xs py-2 rounded bg-[#2F8F6F] text-[#0F0F0F] font-medium hover:bg-[#3aa87f] transition-colors"
            >
              Download .{resultExt}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
